import React, { useCallback, useEffect, useState } from 'react';
import { fetchIncomingCallLogs } from '../services/api';
import { formatDate } from '../utils/helpers';
import '../styles/App.css';

const PER_PAGE = 10;

const EMPTY_FILTERS = { caller: '', receiver: '', call_status: '', start_date: '', end_date: '' };

const na = (value) => (value !== undefined && value !== null && value !== '' ? value : 'N/A');
const when = (value) => (value ? formatDate(value) : 'N/A');

const IncomingCalls = () => {
  const [draft, setDraft] = useState(EMPTY_FILTERS);      // what is typed in the form
  const [applied, setApplied] = useState(EMPTY_FILTERS);  // what the table is filtered by
  const [page, setPage] = useState(1);
  const [calls, setCalls] = useState([]);
  const [pagination, setPagination] = useState({ total_items: 0, total_pages: 1, has_next: false, has_prev: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchIncomingCallLogs({ ...applied, page, per_page: PER_PAGE });
      setCalls(response.data || []);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (err) {
      setCalls([]);
      setError(err.message || 'An error occurred while loading incoming calls');
    } finally {
      setLoading(false);
    }
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (field, value) => setDraft((previous) => ({ ...previous, [field]: value }));

  const handleSearch = () => {
    setPage(1);
    setApplied({ ...draft });
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTERS);
    setPage(1);
    setApplied({ ...EMPTY_FILTERS });
  };

  const startItem = pagination.total_items === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const endItem = Math.min(page * PER_PAGE, pagination.total_items);

  return (
    <div className="content">
      <div className="main-content">
        <h1 className="page-title">Incoming Calls</h1>

        {error && (
          <div role="alert" style={{ padding: '12px 16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label" htmlFor="incoming-caller">Caller Number</label>
              <input id="incoming-caller" type="text" className="filter-input" value={draft.caller}
                     onChange={(e) => setField('caller', e.target.value)} placeholder="Enter caller number" />
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="incoming-receiver">Receiver Number</label>
              <input id="incoming-receiver" type="text" className="filter-input" value={draft.receiver}
                     onChange={(e) => setField('receiver', e.target.value)} placeholder="Enter receiver number" />
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="incoming-status">Call Status</label>
              <select id="incoming-status" className="filter-input" value={draft.call_status}
                      onChange={(e) => setField('call_status', e.target.value)}>
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="busy">Busy</option>
                <option value="no_answer">No Answer</option>
                <option value="user_rejected">Rejected</option>
                <option value="caller_canceled">Caller Canceled</option>
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label" htmlFor="incoming-start">Start Date</label>
              <input id="incoming-start" type="date" className="filter-input" value={draft.start_date}
                     max={draft.end_date || undefined} onChange={(e) => setField('start_date', e.target.value)} />
            </div>
            <div className="filter-group">
              <label className="filter-label" htmlFor="incoming-end">End Date (defaults to today)</label>
              <input id="incoming-end" type="date" className="filter-input" value={draft.end_date}
                     min={draft.start_date || undefined} onChange={(e) => setField('end_date', e.target.value)} />
            </div>
            <div className="filter-group" />
          </div>

          <div className="filter-actions">
            <button type="button" className="reset-button" onClick={handleReset}>Reset</button>
            <button type="button" className="search-button" onClick={handleSearch}>Search</button>
          </div>
        </div>

        <div className="table-container">
          <table className="call-logs-table">
            <thead>
              <tr className="table-header-row">
                <th className="table-header">#</th>
                <th className="table-header">DATE & TIME</th>
                <th className="table-header">CALLER</th>
                <th className="table-header">CALLER COUNTRY</th>
                <th className="table-header">RECEIVER DID</th>
                <th className="table-header">RECEIVER REG. NUMBER</th>
                <th className="table-header">CALL STATUS</th>
                <th className="table-header">DURATION (SEC)</th>
                <th className="table-header">HANGUP CAUSE</th>
                <th className="table-header">STATUS CODE</th>
                <th className="table-header">SIP DISPOSITION</th>
                <th className="table-header">START TIME</th>
                <th className="table-header">ANSWERED TIME</th>
                <th className="table-header">END TIME</th>
                <th className="table-header">USER ID</th>
                <th className="table-header">UUID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="16" className="loading-cell">Loading...</td></tr>
              ) : calls.length === 0 ? (
                <tr><td colSpan="16" className="empty-cell">No incoming calls found</td></tr>
              ) : (
                calls.map((call) => (
                  <tr key={call.id} className="table-row">
                    <td className="table-cell">{call.id}</td>
                    <td className="table-cell">{when(call.created_at)}</td>
                    <td className="table-cell">{na(call.caller)}</td>
                    <td className="table-cell">
                      {call.caller_country_name ? call.caller_country_name.toUpperCase() : 'N/A'}
                    </td>
                    <td className="table-cell"><span className="phone-number-link">{na(call.receiver_did)}</span></td>
                    <td className="table-cell">{na(call.receiver_registration_number)}</td>
                    <td className="table-cell">
                      <span className={`status-badge status-${call.call_status?.toLowerCase()}`}>{na(call.call_status)}</span>
                    </td>
                    <td className="table-cell">{call.duration ?? 0}</td>
                    <td className="table-cell">{na(call.hangup_cause)}</td>
                    <td className="table-cell">{na(call.sip_status)}</td>
                    <td className="table-cell">{na(call.sip_disposition)}</td>
                    <td className="table-cell">{when(call.start_time)}</td>
                    <td className="table-cell">{when(call.answered_time)}</td>
                    <td className="table-cell">{when(call.end_time)}</td>
                    <td className="table-cell">{na(call.user_id)}</td>
                    <td className="table-cell">{na(call.uuid)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total_items > 0 && (
          <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fff', borderRadius: '4px', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Showing {startItem}–{endItem} of {pagination.total_items} calls (Page {page} of {pagination.total_pages})
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={() => setPage((p) => p - 1)} disabled={!pagination.has_prev}
                      style={{ padding: '6px 12px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px',
                               backgroundColor: pagination.has_prev ? '#fff' : '#f5f5f5',
                               cursor: pagination.has_prev ? 'pointer' : 'not-allowed' }}>
                Previous
              </button>
              <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!pagination.has_next}
                      style={{ padding: '6px 12px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px',
                               backgroundColor: pagination.has_next ? '#fff' : '#f5f5f5',
                               cursor: pagination.has_next ? 'pointer' : 'not-allowed' }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomingCalls;
